import React, { useContext, useEffect, useRef, useState } from "react";
import { Icon, Pivot, PivotItem, PrimaryButton, TooltipHost } from "@fluentui/react";
import { t } from "i18next";
import studylistContext from "../../../../context/studylist/StudylistContext";
import themeContext from "../../../../context/theme/ThemeContext";
import { TAB_NAME, USERTYPES } from "../../../../constants/Constants";
import ManageLibraryModal from "./ManageLibraryModal";
import { LibraryCategoryFilter } from "./LibraryCategoryFilter";
import { getLibraryCatalog, loadLibraryCatalog, subscribeLibraryCatalog } from "./libraryTagStore";
import "../CXRNavTabs/CXRNavTabs.css";

const FILTER_TAB = "filter";

const LibraryToolbar = () => {
  const { roles, selectedValue, setSearchDto, setRefreshRequired } = useContext(studylistContext);
  const { getThemeColor } = useContext(themeContext);
  const [showManage, setShowManage] = useState(false);
  const [catalog, setCatalog] = useState(getLibraryCatalog);
  const [sel, setSel] = useState({});
  const [tab, setTab] = useState("all-cases");
  const skipFilterRefresh = useRef(true);

  useEffect(() => subscribeLibraryCatalog(setCatalog), []);

  useEffect(() => {
    if (roles?.[0] !== USERTYPES.RADIOLOGIST || selectedValue !== "study_10") return undefined;
    loadLibraryCatalog();
    return undefined;
  }, [roles, selectedValue]);

  useEffect(() => {
    if (skipFilterRefresh.current) {
      skipFilterRefresh.current = false;
      return undefined;
    }
    const viewingFilter = tab === FILTER_TAB;
    const untagged = viewingFilter && sel.untagged === "in";
    const ids = viewingFilter
      ? Object.keys(sel).filter((id) => id !== "untagged" && sel[id] === "in").map((id) => Number(id))
      : [];
    setSearchDto((prev) => ({
      ...prev,
      libraryTagIds: ids.length ? ids : null,
      libraryCategoryIds: null,
      libraryUntagged: untagged ? true : null,
    }));
    setTimeout(() => {
      setRefreshRequired((prev) => ({
        ...prev,
        [TAB_NAME.LIBRARY]: { ...prev[TAB_NAME.LIBRARY], alreadyLoaded: false },
      }));
    }, 10);
    return undefined;
  }, [sel, tab]);

  const hasFilter = Object.keys(sel).some((id) => sel[id] === "in");
  const selectedTab = hasFilter && tab === FILTER_TAB ? FILTER_TAB : "all-cases";

  const changeSel = (next) => {
    setSel(next);
    setTab(Object.keys(next).some((id) => next[id] === "in") ? FILTER_TAB : "all-cases");
  };

  if (roles?.[0] !== USERTYPES.RADIOLOGIST || selectedValue !== "study_10") {
    return null;
  }

  return (
    <>
      <div style={{ background: "var(--studylist-search-and-tab-section-background)" }}>
        <div
          style={{
            position: "relative",
            borderLeft: getThemeColor("border"),
            borderTop: getThemeColor("border"),
            borderRight: getThemeColor("border"),
            background: "var(--background-theme-color)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
          className="xray-tabs-pivot-top"
        >
          <div className="study-list-hide-cxr-border-top"></div>
          <div className="library-filter-tab-row">
          <Pivot
            className="xray-tabs-pivot"
            aria-label="Library tabs"
            linkSize="small"
            linkFormat="tabs"
            selectedKey={selectedTab}
            onLinkClick={(item) => {
              const key = item?.props?.itemKey;
              if (key === "all-cases" || key === FILTER_TAB) setTab(key);
            }}
          >
            <PivotItem
              headerText={t("study-list.library-tag.all-cases")}
              itemKey="all-cases"
            />
            {hasFilter ? (
              <PivotItem
                itemKey={FILTER_TAB}
                headerText={
                  <span className="library-filter-tab">
                    <span className="library-filter-tab-label">{t("study-list.library-tag.filtered")}</span>
                    <TooltipHost content={t("button.close")} id="library-filter-close">
                    <span
                      className="library-filter-tab-close"
                      role="button"
                      aria-label={t("button.close")}
                      onMouseDown={(event) => event.stopPropagation()}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        changeSel({});
                      }}
                    >
                      <Icon iconName="Cancel" />
                    </span>
                    </TooltipHost>
                  </span>
                }
              />
            ) : null}
          </Pivot>
          <LibraryCategoryFilter
            tags={catalog.tags}
            categories={catalog.categories}
            sel={sel}
            setSel={changeSel}
          />
          </div>
          <TooltipHost content={t("study-list.library-tag.manage-tooltip")} id="library-manage">
          <PrimaryButton
            text={t("study-list.library-tag.manage")}
            ariaLabel={t("study-list.library-tag.manage-tooltip")}
            iconProps={{ iconName: "Equalizer" }}
            onClick={() => setShowManage(true)}
            styles={{ root: { marginRight: 10, flexShrink: 0 } }}
          />
          </TooltipHost>
        </div>
      </div>
      <ManageLibraryModal show={showManage} onDismiss={() => setShowManage(false)} />
    </>
  );
};

export default LibraryToolbar;
